import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import HighlightText from './HighlightText';

const { FiAward, FiCheck, FiArrowRight, FiZap, FiTarget, FiBookOpen, FiTrendingUp, FiPhone, FiMessageCircle, FiX } = FiIcons;

const ContactModal = ({ onClose }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
    <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="h-1.5 bg-gradient-to-r from-primary via-orange-400 to-yellow-400 rounded-t-3xl" />
      <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition">
        <FiX className="w-4 h-4 text-gray-600" />
      </button>
      <div className="p-8 text-center">
        <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <FiPhone className="w-7 h-7 text-primary" />
        </div>
        <h3 className="text-xl font-extrabold text-slate-900 mb-1">Get in Touch</h3>
        <p className="text-sm text-gray-500 font-medium mb-6">Choose how you'd like to connect with us</p>
        <div className="flex flex-col gap-3">
          <a href="tel:+919415905584" className="flex items-center justify-center gap-3 bg-primary text-white px-6 py-4 rounded-2xl font-extrabold hover:bg-primary-hover transition-all shadow-lg text-sm tracking-wide">
            <FiPhone className="w-5 h-5" /> Call: +91 9415905584
          </a>
          <a href="https://wa.me/919415905584" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-green-500 text-white px-6 py-4 rounded-2xl font-extrabold hover:bg-green-600 transition-all shadow-lg text-sm tracking-wide">
            <FiMessageCircle className="w-5 h-5" /> WhatsApp Us
          </a>
        </div>
      </div>
    </div>
  </div>
);

const cards = [
  {
    icon: FiBookOpen,
    badge: 'CBSE / ICSE',
    title: 'Crack CBSE & ICSE with Confidence',
    desc: 'Stop struggling with concepts—get crystal-clear understanding with one-on-one guidance tailored to your syllabus.',
    features: [
      'Personalized attention for faster improvement',
      'Concept clarity + exam-focused preparation',
      'Regular progress tracking & doubt solving',
    ],
    cta: 'Connect With Us',
    color: 'from-red-400 to-orange-400',
    lightBg: 'bg-red-50',
    textColor: 'text-red-500',
    borderColor: 'border-red-100',
    accentBg: 'bg-gradient-to-br from-[#fff5f5] to-[#ffeaea]',
    img: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=600&q=80',
  },
  {
    icon: FiTarget,
    badge: 'JEE / NEET',
    title: "Serious About JEE / NEET? Let's Get You There.",
    desc: 'Train with expert mentors who know exactly what it takes to crack competitive exams.',
    features: [
      'Proven strategies for top ranks',
      'Intensive practice + doubt sessions',
      'Structured roadmap to success',
    ],
    cta: 'Connect With Us',
    color: 'from-purple-500 to-indigo-500',
    lightBg: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-100',
    accentBg: 'bg-gradient-to-br from-[#f5f3ff] to-[#ede9fe]',
    img: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=600&q=80',
  },
];

const ServicesSection = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      {showModal && <ContactModal onClose={() => setShowModal(false)} />}
      <section className="py-24 bg-white" id="classes">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-red-50 text-primary text-xs font-bold px-4 py-2 rounded-full mb-4 border border-red-100 uppercase tracking-widest">
              <FiAward className="w-3.5 h-3.5" /> One-to-One Tutoring
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
              Explore Our <HighlightText>Services</HighlightText>
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto font-medium">
              Personalized tutoring programs designed for every student's unique needs and goals.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {cards.map((card, idx) => (
              <div
                key={idx}
                className={`group rounded-3xl border ${card.borderColor} shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={card.img} alt={card.badge} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className={`absolute top-4 left-4 bg-gradient-to-r ${card.color} text-white text-xs font-extrabold px-3 py-1.5 rounded-full tracking-widest uppercase shadow`}>
                    {card.badge}
                  </div>
                </div>

                <div className={`flex flex-col flex-grow p-8 ${card.accentBg}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 rounded-xl ${card.lightBg} flex items-center justify-center flex-shrink-0`}>
                      <card.icon className={`w-5 h-5 ${card.textColor}`} />
                    </div>
                    <h3 className="text-xl font-extrabold text-slate-900 leading-snug tracking-tight">{card.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 text-sm leading-relaxed font-medium mb-6">{card.desc}</p>
                  
                  <div className="space-y-3 mb-8">
                    {card.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <FiCheck className="w-3 h-3 text-green-600 stroke-[3]" />
                        </div>
                        <span className="text-sm font-semibold text-slate-700">{f}</span>
                      </div>
                    ))}
                  </div>

                  {/* Single action button */}
                  <div className="mt-auto">
                    <button
                      onClick={() => setShowModal(true)}
                      className={`w-full flex items-center justify-center gap-2 bg-gradient-to-r ${card.color} text-white px-5 py-4 rounded-2xl font-extrabold hover:opacity-90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform tracking-wide text-sm`}
                    >
                      {idx === 0 ? <FiZap className="w-4 h-4" /> : <FiTrendingUp className="w-4 h-4" />}
                      {card.cta}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesSection;